import type { PayerFileClaimAcknowledgmentGridRow } from '@cumu/shared';
import { controller } from '@github/catalyst';
import type AgGrid from 'ag-grid-enterprise';
import {
  ClientSideGridElement,
  ClientSideGridOptions
} from './client-side-grid-element';
import {
  codeDescription,
  formatUTCDate,
  linkRenderer,
  renderCodeDescription
} from './grid-helpers';

@controller('payer-file-claim-acknowledgments')
class PayerFileClaimAcknowledgmentsElement extends ClientSideGridElement<PayerFileClaimAcknowledgmentGridRow> {
  protected getGridOptions(): ClientSideGridOptions<PayerFileClaimAcknowledgmentGridRow> {
    const columnDefs: AgGrid.ColDef<PayerFileClaimAcknowledgmentGridRow>[] = [
      {
        field: 'submitter_claim_control_number',
        headerName: 'Claim',
        cellRenderer: ({
          data,
          value
        }: AgGrid.ICellRendererParams<PayerFileClaimAcknowledgmentGridRow>) =>
          linkRenderer(data?.claim_href, value, 'View claim'),
        width: 135,
        minWidth: 135,
        suppressSizeToFit: true
      },
      {
        colId: 'patient',
        headerName: 'Patient',
        valueGetter: ({ data }) => patientName(data),
        tooltipValueGetter: ({ data }) => patientName(data),
        flex: 1,
        minWidth: 190
      },
      {
        field: 'effective_date',
        headerName: 'Effective',
        valueFormatter: ({ value }) => formatUTCDate(value),
        width: 130
      },
      {
        colId: 'category',
        headerName: 'Category',
        valueGetter: ({ data }) =>
          codeDescription(data?.category_code, data?.category_description),
        cellRenderer: ({
          data
        }: AgGrid.ICellRendererParams<PayerFileClaimAcknowledgmentGridRow>) =>
          renderCodeDescription(
            data?.category_code,
            data?.category_description
          ),
        tooltipField: 'category_description',
        wrapText: true,
        autoHeight: true,
        flex: 2,
        minWidth: 360
      },
      {
        colId: 'status',
        headerName: 'Status',
        valueGetter: ({ data }) =>
          codeDescription(data?.status_code, data?.status_description),
        cellRenderer: ({
          data
        }: AgGrid.ICellRendererParams<PayerFileClaimAcknowledgmentGridRow>) =>
          renderCodeDescription(data?.status_code, data?.status_description),
        tooltipField: 'status_description',
        wrapText: true,
        autoHeight: true,
        flex: 1,
        minWidth: 250
      },
      {
        field: 'status_message',
        headerName: 'Message',
        tooltipField: 'status_message',
        wrapText: true,
        autoHeight: true,
        flex: 2,
        minWidth: 320
      },
      {
        field: 'payer_claim_control_number',
        headerName: 'Payer claim control',
        tooltipField: 'payer_claim_control_number',
        width: 220
      },
      {
        field: 'clearinghouse_trace_number',
        headerName: 'Trace',
        tooltipField: 'clearinghouse_trace_number',
        width: 180
      }
    ];

    return {
      getRowId: ({ data }) => data.claim_acknowledgment_entry_id,
      columnDefs
    };
  }
}

function patientName(
  row: PayerFileClaimAcknowledgmentGridRow | null | undefined
) {
  return [row?.patient_first_name, row?.patient_last_name]
    .filter(Boolean)
    .join(' ');
}

declare global {
  interface Window {
    PayerFileClaimAcknowledgmentsElement: typeof PayerFileClaimAcknowledgmentsElement;
  }
  interface HTMLElementTagNameMap {
    'payer-file-claim-acknowledgments': PayerFileClaimAcknowledgmentsElement;
  }
}
